import { useState } from 'react';
import { Card, CardDescription } from '@/components/ui/card';
import { WindowTabs, type BriefWindow } from '@/components/common/WindowTabs';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHero, KpiStat } from '@/components/common/PageHero';
import { PerfCell } from '@/components/stocks/PerfCell';
import { Sparkline } from '@/components/stocks/Sparkline';
import { useStocks } from '@/lib/queries';
import { renderBriefMarkdown } from '@/lib/brief-renderer';
import type { StockPerfDto } from '@/types/dto';

const bucketOrder: Record<string, number> = {
  self: 0, commodity: 1, industry: 2, competitor: 3, contractor: 4, operator: 5, partner: 6,
};

const bucketLabel: Record<string, string> = {
  self: 'HMH',
  commodity: 'Oil & Gas Futures',
  industry: 'Industry ETFs',
  competitor: 'Competitors (Drilling-Digital Vendors)',
  contractor: 'Drilling Contractors',
  operator: 'Operators (E&P)',
  partner: 'Partners',
};

export default function Stocks() {
  const [window, setWindow] = useState<BriefWindow>('7d');
  const { data, isLoading, error } = useStocks(window);

  const gainers = data?.perf.filter((p) => (p.chg5D ?? 0) > 0).length ?? 0;
  const losers = data?.perf.filter((p) => (p.chg5D ?? 0) < 0).length ?? 0;
  const topMove = data?.perf.reduce(
    (best, p) => (Math.abs(p.chg5D ?? 0) > Math.abs(best?.chg5D ?? 0) ? p : best),
    null as StockPerfDto | null
  );

  return (
    <div>
      <PageHero
        eyebrow="Stock Monitor"
        title={<>The tape <span className="text-muted font-normal">across the value chain</span></>}
        lede="HMH, drilling contractors, operators, competitors, partners, and the underlying oil-and-gas benchmarks — in one read. Per-row deltas stay fixed at 1d / 5d / 30d; the Market Read narrative below shifts with the window you pick."
        stats={
          data && data.perf.length > 0 && (
            <dl className="flex gap-8 md:gap-12 flex-wrap">
              <KpiStat label="Tickers" value={data.perf.length} tone="accent" />
              <KpiStat label="5d ▲" value={gainers} />
              <KpiStat label="5d ▼" value={losers} />
              {topMove && (
                <KpiStat
                  label="Top 5d move"
                  value={`${topMove.symbol} ${(topMove.chg5D! >= 0 ? '+' : '')}${topMove.chg5D!.toFixed(1)}%`}
                />
              )}
            </dl>
          )
        }
      />

      {data?.analysis && (
        <Card>
          <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-2xl font-semibold">Market Read</h2>
              <span className="text-muted text-xs tracking-widest font-(family-name:--font-ui)">
                {data.analysis.analysisDate}
              </span>
            </div>
            <WindowTabs value={window} onChange={setWindow} />
          </div>
          <div
            className="brief-prose brief-drop font-(family-name:--font-body) text-[0.95rem] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBriefMarkdown(data.analysis.narrative ?? '', {}) }}
          />
        </Card>
      )}

      {isLoading && <LoadingSkeleton lines={6} />}
      {error && <ErrorState message={(error as Error).message} />}

      {data && data.perf.length === 0 && (
        <Card>
          <CardDescription>
            No price data yet. Trigger "stock-prices" from Debug → Triggers, then "stock-analysis".
          </CardDescription>
        </Card>
      )}

      {data && data.perf.length > 0 && (
        <>
          {Object.entries(
            data.perf.reduce<Record<string, StockPerfDto[]>>((acc, row) => {
              (acc[row.bucket] ??= []).push(row);
              return acc;
            }, {})
          )
            .sort(([a], [b]) => (bucketOrder[a] ?? 9) - (bucketOrder[b] ?? 9))
            .map(([bucket, rows]) => (
              <Card key={bucket}>
                <div className="flex items-baseline gap-2 mb-4 flex-wrap">
                  <h2 className="text-2xl font-semibold">{bucketLabel[bucket] ?? bucket}</h2>
                  <span className="text-muted text-sm">({rows.length})</span>
                </div>

                {/* Desktop */}
                <table className="hidden md:table w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-accent">
                      <th style={{ width: 110 }} className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Ticker</th>
                      <th className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Name</th>
                      <th style={{ width: 110 }} className="py-2 px-3 text-right text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Last</th>
                      <th style={{ width: 100 }} className="py-2 px-3 text-center text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Trend</th>
                      <th style={{ width: 80 }} className="py-2 px-3 text-right text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">1d</th>
                      <th style={{ width: 80 }} className="py-2 px-3 text-right text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">5d</th>
                      <th style={{ width: 80 }} className="py-2 px-3 text-right text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">30d</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows
                      .sort((a, b) => (b.chg5D ?? -999) - (a.chg5D ?? -999))
                      .map((p) => (
                        <tr key={p.symbol} className="border-b border-border hover:bg-surface-hover">
                          <td className="py-3 px-3 font-medium">{p.symbol}</td>
                          <td className="py-3 px-3 text-muted">{p.name}</td>
                          <td className="py-3 px-3 text-right tabular-nums">
                            {p.latestClose.toFixed(2)} {p.currency}
                          </td>
                          <td className="py-3 px-3"><div className="flex justify-center"><Sparkline values={p.spark} positive={p.chg30D == null ? null : p.chg30D >= 0} /></div></td>
                          <td className="py-3 px-3 text-right"><PerfCell value={p.chg1D} /></td>
                          <td className="py-3 px-3 text-right"><PerfCell value={p.chg5D} /></td>
                          <td className="py-3 px-3 text-right"><PerfCell value={p.chg30D} /></td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                {/* Mobile cards */}
                <ul className="md:hidden flex flex-col divide-y divide-border">
                  {rows
                    .sort((a, b) => (b.chg5D ?? -999) - (a.chg5D ?? -999))
                    .map((p) => (
                      <li key={p.symbol} className="py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium">{p.symbol}</div>
                            <div className="text-[0.75rem] text-muted">{p.name}</div>
                            <div className="text-[0.75rem] text-muted tabular-nums mt-0.5">
                              {p.latestClose.toFixed(2)} {p.currency}
                            </div>
                            <div className="flex gap-3 mt-1 text-xs">
                              <span>1d <PerfCell value={p.chg1D} /></span>
                              <span>5d <PerfCell value={p.chg5D} /></span>
                              <span>30d <PerfCell value={p.chg30D} /></span>
                            </div>
                          </div>
                          <div className="hidden sm:block">
                            <Sparkline values={p.spark} positive={p.chg30D == null ? null : p.chg30D >= 0} />
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </Card>
            ))}
        </>
      )}
    </div>
  );
}
